'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
/* eslint-disable react/no-unused-class-component-methods */
import React, { Component, type CSSProperties, type JSX } from 'react';
// eslint-disable-next-line import/no-cycle
import { onUpdateControls, output, resetControls } from './helpers/utils';
import type { LabelObject } from './children/common/FieldLabel';
// eslint-disable-next-line import/no-cycle
import SwitchComponent from './helpers/switchComponents';
// eslint-disable-next-line import/no-cycle
import ContainerControl from './children/Container';
import { FormStyled } from './styled';
import { sumClasses } from '../../utils/misc';

type FormObjectRoom = {
  [key: string]: any;
};

type FormObject = {
  [key: string]: FormObjectRoom;
};

export type Option = {
  value?: any;
  label?: string | JSX.Element;
  type?: { [key: string]: string | any };
  selectedClassName?: string;
  style?: CSSProperties;
  disabled?: boolean;
  className?: string;
  addLabel?: string;
  customSelectedIcon?: JSX.Element;
  hide?: boolean;
  unselectOther?: boolean;
};

/** Arguments a `valueLabel` callback is called with; a callback may declare only the ones it needs. */
export type ValueLabelArgs = {
  minValue: number;
  maxValue: number;
  minAllowed: number;
  maxAllowed: number;
};

export type ValueLabel = (args: ValueLabelArgs) => string | JSX.Element;

export enum ControlType {
  SLIDER = 'slider',
  LABEL = 'label',
  EXTERNAL = 'external',
  CHECKBOX = 'check',
  RADIO = 'radio',
  LISTBOX = 'listbox',
  CONTAINER = 'container',
  SEARCH_LIST = 'searchlist',
}

export enum FieldType {
  TEXT = 'text',
  HIDDEN = 'hidden',
  PASSWORD = 'password',
  LISTBOX = 'listbox',
  MULTILISTBOX = 'multilistbox',
  TOGGLE = 'toggle',
}

export type Target = {
  target: {
    name: string;
    value?: boolean | string | number | string[] | number[] | any;
    clear?: boolean;
  };
};

export type ControlProps = {
  addLabel?: string;
  control?: ControlType;
  className?: string;
  style?: CSSProperties;
  label?: LabelObject | string | JSX.Element;
  placeholder?: string;
  name?: string;
  value?:
    | any
    | {
        min: number;
        max: number;
      };
  type?: FieldType;
  unit?: string;
  disabled?: boolean;
  icon?: JSX.Element;
  clear?: boolean;
  content?: string | JSX.Element;
  minAllowed?: number;
  maxAllowed?: number;
  bulletSize?: number;
  defaultMin?: number;
  defaultMax?: number;
  valueLabel?: string | ValueLabel;
  roundTo?: number;
  customSvg?: any;
  options?: Option[];
  default?: any;
  hideRadio?: boolean;
  minEl?: number;
  equalTo?: string;
  exclude?: boolean;
  hide?: boolean;
  valueAsObject?: boolean;
  controls?: ControlProps[];
  requiredKey?: string;
  greaterThan?: string;
  top?: {
    text: string | JSX.Element;
  };
  controlProps?: {
    className?: string;
    controlsClassName?: string;
    height?: number;
    closeOnClickOut?: boolean;
    hasCta?: boolean;
    clearAll?: string;
    showMoreLabel?: string;
    hasArrows?: boolean;
  };
  expanded?: boolean;
  hideMin?: boolean;
  hideClearAll?: boolean;
  contentBefore?: JSX.Element;
  contentAfter?: JSX.Element;
  component?: any;
  limit?: number;
  width?: number;
  valuesOnBottom?: boolean;
  columns?: number;
  noResultsText?: string;
  isMobile?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | undefined | null>;
};

export type UpdateOnChangeProps = {
  e: Target;
  formObject?: FormObject;
  updatedControls: ControlProps[];
};

export type FormProps = {
  controls: ControlProps[];
  updateOnChange?: ({ e, formObject, updatedControls }: UpdateOnChangeProps) => Promise<ControlProps[]>;
  formClassName?: string;
  resetKeyOnUpdate?: boolean;
};

export type StateProps = {
  key: number;
  controls: ControlProps[];
};

export default class Form extends Component<FormProps, StateProps> {
  constructor(props: FormProps) {
    super(props);
    const { controls } = this.props;

    this.state = {
      key: 0,
      controls,
    };
  }

  async onUpdate(e: any) {
    const { resetKeyOnUpdate } = this.props;
    const { controls } = this.state;

    let updatedControls = controls.map((item: any) => {
      if (item.control === ControlType.CONTAINER) {
        const subItemControls = item.controls.map((subItem: any) => onUpdateControls(e, subItem));
        item.controls = subItemControls;
        return onUpdateControls(e, item);
      } else {
        return onUpdateControls(e, item);
      }
    });

    if (this.props.updateOnChange) {
      const { updateOnChange } = this.props;
      const formObject = output(updatedControls);
      const response = await updateOnChange({ e, formObject, updatedControls });

      if (response) {
        updatedControls = response;
      }
    }
    this.setState({
      controls: updatedControls,
    });
    if (resetKeyOnUpdate) {
      this.setState((prev) => ({ key: prev.key + 1 }));
    }
  }

  updateControls(updatedControls: ControlProps[]) {
    this.setState({
      controls: updatedControls,
    });
  }

  resetForm() {
    const { controls } = this.state;
    let updatedControls = controls.map((a) => ({ ...a }));

    updatedControls = updatedControls.map((item: any) => {
      if (item.control === ControlType.CONTAINER) {
        const subItemControls = item.controls.map((subItem: any) => resetControls(subItem));
        item.controls = subItemControls;
        return resetControls(item);
      } else {
        return resetControls(item);
      }
    });

    this.setState((prev) => ({
      key: prev.key + 1,
      controls: updatedControls,
    }));
  }

  refreshForm() {
    this.setState((prev) => ({ key: prev.key + 1 }));
  }

  returnItem(item: ControlProps, containerRef?: any, index?: number) {
    const { control, name, component } = item;
    return React.createElement(SwitchComponent({ control, component }), {
      ...item,
      onUpdate: (e: any) => this.onUpdate(e),
      ref: name && (this as any)[name],
      // @ts-ignore
      key: `${name}${item?.label?.text ? `-${item?.label?.text}` : ''}` || index,
      ...(containerRef ? { containerRef } : {}),
    });
  }

  returnContainer(item: ControlProps, i: number) {
    const {
      name,
      controls: subControls,
      controlProps,
      contentBefore,
      contentAfter,
      top,
      control,
      limit,
      columns,
    } = item;

    if (name) {
      return (
        <ContainerControl
          key={name || i}
          {...{
            control,
            name,
            className: controlProps && controlProps.className,
            top,
            controlsClassName: controlProps && controlProps.controlsClassName,
            height: controlProps && controlProps.height,
            contentBefore,
            subControls: subControls || [],
            returnItem: this.returnItem.bind(this),
            contentAfter,
            closeOnClickOut: controlProps && controlProps.closeOnClickOut,
            hasCta: controlProps && controlProps.hasCta,
            clearAll: controlProps && controlProps.clearAll,
            showMoreLabel: controlProps && controlProps.showMoreLabel,
            hasArrows: controlProps && controlProps.hasArrows,
            hasTwoColumns: columns === 2,
            onUpdate: (e: Target) => this.onUpdate(e),
            limit,
          }}
        />
      );
    }
    return null;
  }

  render() {
    const { formClassName } = this.props;
    const { controls, key } = this.state;

    return (
      <FormStyled key={key} className={sumClasses(['oss-ui-form', formClassName || ''])}>
        {controls.map((item: ControlProps, i: number) => {
          const { control, name, hide } = item;
          if (hide) return null;
          if (control === ControlType.CONTAINER && name) {
            return this.returnContainer(item, i);
          } else {
            return this.returnItem(item, undefined, i);
          }
        })}
      </FormStyled>
    );
  }
}
